/**
 * The interpolation behind EAV.RDP.INTERP. LSEG computes it in Adfin (its
 * `AdInterp(x, xs, ys, method)`, compiled into the Excel add-in); this is our
 * own code, never LSEG's, and each method is in `METHODS` only once it gives
 * LSEG's double exactly on every golden (docs/lseg-golden/outputs/interp_*).
 *
 * The input is what InterpProcessor (interp.ts) sends: numbers, at least the
 * method's minimum of points, none of them invalid.
 */

import { crExp, crLog } from "./correctlyRounded";
import { splrep, splrepMatched } from "./interpSmoothing";
import { tspline, tsplineMatched, tsplinex } from "./interpTension";

/** The methods Adfin accepts (golden 2026-10-27). Any other gives ERROR 9042. */
// LSEG: Adfin (22038) AdInterp (its method names, as captured in docs/lseg-golden/outputs/interp_methods_out.json)
export const LSEG_METHODS: ReadonlySet<string> = new Set(
  [
    "LIN",
    "STEP",
    "STEPX",
    "STEPRCYES",
    "CUBD",
    "CUBR",
    "CUBS",
    "CUBX",
    "TSPLINE",
    "TSPLINEX",
    "SPLREP",
    "AKIMA",
    "HERMITE",
    "MONOTONICHERMITE",
    "NATURALCUBICSPLINE",
    "MONOTONICCUBICNATURALSPLINE",
    "LOG",
  ].map((code) => `IM:${code}`),
);

// LSEG: Adfin (22038) AdInterp (its error texts, from the goldens)
const NOT_SUPPORTED = "ERROR 9042 - Interpolation method is not supported.";
const NULL_DATE = "ERROR 8788 - DateArray : null date in array.";
const NOT_SORTED = "ERROR 8795 - Input Array: First column values must be sorted.";

/** A method: the Y value at `x` on the points (xs, ys), or the text of the Adfin error. */
export type Interpolator = (x: number, xs: number[], ys: number[]) => number | { error: string };

/**
 * Linear between the two points around x, flat outside them. Written as
 * slope and intercept (`a + b * x`), the form that gives LSEG's last digits:
 * blank_x is 0.699999999999999 (not 0.7) and the date goldens differ from the
 * exact line by 1e-14, both of which the other forms miss.
 * Duplicate X values (golden d9, X 1,2,4,4,7,10): the X itself gives the
 * first of their Ys, and the later point starts the segment after it. That
 * holds at the last X too (golden d24, X 1,2,10,10, Y 1,2,3,4: 3 at x = 10),
 * so a node is looked up before the flat ends; above the range it is the
 * last Y (4).
 */
// LSEG: Adfin (22038) AdInterp IM:LIN (behaviour from the goldens; the arithmetic order found by matching them)
function lin(x: number, xs: number[], ys: number[]): number {
  const last = xs.length - 1;
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  if (x < xs[0]) return ys[0];
  if (x > xs[last]) return ys[last];
  let i = 0;
  while (xs[i + 1] <= x) i++;
  const [x0, x1, y0, y1] = [xs[i], xs[i + 1], ys[i], ys[i + 1]];
  const slope = (y1 - y0) / (x1 - x0);
  const intercept = (y0 * x1 - y1 * x0) / (x1 - x0);
  return intercept + slope * x;
}

/**
 * The index of the step that holds x: the last point at or before x. Below the
 * first X it is 0 (the first point's Y, flat); above the last X, the last.
 * Of duplicate X values the first holds the step (golden d9, X 1,2,4,4,7,10,
 * Y 1,...,6: x from 4 up to 7 gives 3, not 4, for STEP and for STEPX, golden
 * d9_stepx), at a repeated last X and above it too (d24, X 1,2,10,10, Y
 * 1,2,3,4: 3).
 */
function stepIndex(x: number, xs: number[]): number {
  let i = 0;
  while (i + 1 < xs.length && xs[i + 1] <= x) i++;
  while (i > 0 && xs[i - 1] === xs[i]) i--;
  return i;
}

/**
 * Each point's Y holds from its X up to the next X (a node gives its own Y);
 * flat at the first Y below the range and at the last Y above it.
 */
// LSEG: Adfin (22038) AdInterp IM:STEP (behaviour from the dense goldens: nodes, between, below, above; d9, d24)
function step(x: number, xs: number[], ys: number[]): number {
  return ys[stepIndex(x, xs)];
}

/** STEP, but 0 below the first X (the first X itself gives the first Y). */
// LSEG: Adfin (22038) AdInterp IM:STEPX (behaviour from the dense goldens, d9_stepx and d24 included)
function stepx(x: number, xs: number[], ys: number[]): number {
  return x < xs[0] ? 0 : step(x, xs, ys);
}

/**
 * The step is held by the point at its right end: x in (X[i-1], X[i]] gives
 * Y[i], so a node gives its own Y and a point between nodes gives the next
 * point's Y. Two edges, as the goldens show them: the first X gives the first
 * Y, but below the first X the answer is the second point's Y (not the
 * first's); above the last X it is the last X's Y, as at the last X itself:
 * the first of its points when it is repeated (golden d24, X 1,2,10,10, Y
 * 1,2,3,4: 3 above the range, not 4).
 */
// LSEG: Adfin (22038) AdInterp IM:STEPRCYES (behaviour from the dense goldens: d1, d2b, d9 and d24, below, at and above the range)
function steprcyes(x: number, xs: number[], ys: number[]): number {
  if (x === xs[0]) return ys[0];
  if (x < xs[0]) return ys[1];
  const last = xs.length - 1;
  const at = Math.min(x, xs[last]);
  let i = 1;
  while (i < last && xs[i] < at) i++;
  return ys[i];
}

/** The segment that holds x: i with xs[i] <= x < xs[i+1]; the first below the range, the last at and above its end. */
function segment(x: number, xs: number[]): number {
  let i = 0;
  while (i < xs.length - 2 && xs[i + 1] <= x) i++;
  return i;
}

/**
 * The first derivatives of the natural cubic spline (second derivative 0 at
 * both ends), from the tridiagonal system in the derivatives:
 *   2 d0 + d1 = 3 S0,  h_i d_{i-1} + 2 (h_{i-1} + h_i) d_i + h_{i-1} d_{i+1} = 3 (h_i S_{i-1} + h_{i-1} S_i),
 *   d_{n-2} + 2 d_{n-1} = 3 S_{n-2},
 * with h_i the X steps and S_i the secant slopes, solved by elimination
 * forward and substitution back.
 */
function naturalDerivatives(xs: number[], ys: number[]): { d: number[]; h: number[]; s: number[] } {
  const n = xs.length;
  const h: number[] = [];
  const s: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    h.push(xs[i + 1] - xs[i]);
    s.push((ys[i + 1] - ys[i]) / h[i]);
  }
  const lower = new Array<number>(n).fill(0);
  const diag = new Array<number>(n).fill(2);
  const upper = new Array<number>(n).fill(0);
  const rhs = new Array<number>(n);
  upper[0] = 1;
  rhs[0] = 3 * s[0];
  for (let i = 1; i < n - 1; i++) {
    lower[i] = h[i];
    diag[i] = 2 * (h[i] + h[i - 1]);
    upper[i] = h[i - 1];
    rhs[i] = 3 * (h[i] * s[i - 1] + h[i - 1] * s[i]);
  }
  lower[n - 1] = 1;
  rhs[n - 1] = 3 * s[n - 2];
  const d = new Array<number>(n);
  const ratio = new Array<number>(n);
  let pivot = diag[0];
  d[0] = rhs[0] / pivot;
  for (let j = 1; j < n; j++) {
    ratio[j] = upper[j - 1] / pivot;
    pivot = diag[j] - lower[j] * ratio[j];
    d[j] = (rhs[j] - lower[j] * d[j - 1]) / pivot;
  }
  for (let j = n - 2; j >= 0; j--) d[j] -= ratio[j + 1] * d[j + 1];
  return { d, h, s };
}

/**
 * Hyman's monotonicity filter (Hyman 1983, with the extension of Dougherty,
 * Edelman & Hyman 1989) on the spline's derivatives: each derivative keeps its
 * sign only if it agrees with the local slope estimate and is cut to 3 times
 * the smallest neighbouring slope (the bound that keeps a monotone stretch
 * monotone), a bound relaxed to 1.5 times the one-sided parabola slopes where
 * the data's curvature allows; an end derivative is cut to 3 times the end slope.
 */
function hymanFilter(d: number[], h: number[], s: number[]): number[] {
  const n = d.length;
  const out = d.slice();
  const cut = (v: number, bound: number) => Math.sign(v) * Math.min(Math.abs(v), bound);
  for (let i = 0; i < n; i++) {
    if (i === 0 || i === n - 1) {
      const end = i === 0 ? s[0] : s[n - 2];
      out[i] = d[i] * end > 0 ? cut(d[i], Math.abs(3 * end)) : 0;
      continue;
    }
    const pm = (s[i - 1] * h[i] + s[i] * h[i - 1]) / (h[i - 1] + h[i]);
    let bound = 3 * Math.min(Math.abs(s[i - 1]), Math.abs(s[i]), Math.abs(pm));
    if (i > 1 && (s[i - 1] - s[i - 2]) * (s[i] - s[i - 1]) > 0) {
      const pd = (s[i - 1] * (2 * h[i - 1] + h[i - 2]) - s[i - 2] * h[i - 1]) / (h[i - 2] + h[i - 1]);
      if (pm * pd > 0 && pm * (s[i - 1] - s[i - 2]) > 0) bound = Math.max(bound, 1.5 * Math.min(Math.abs(pm), Math.abs(pd)));
    }
    if (i < n - 2 && (s[i] - s[i - 1]) * (s[i + 1] - s[i]) > 0) {
      const pu = (s[i] * (2 * h[i] + h[i + 1]) - s[i + 1] * h[i]) / (h[i] + h[i + 1]);
      if (pm * pu > 0 && -pm * (s[i] - s[i - 1]) > 0) bound = Math.max(bound, 1.5 * Math.min(Math.abs(pm), Math.abs(pu)));
    }
    out[i] = d[i] * pm > 0 ? cut(d[i], bound) : 0;
  }
  return out;
}

/**
 * The cubic Hermite piece of segment i (the one that holds x, the end ones
 * continued outside the range) at x, as y_i + t (a + t (b + t c)) with
 * t = x - x_i. A node gives its own Y.
 */
function hermiteAt(x: number, xs: number[], ys: number[], d: number[], h: number[], s: number[]): number {
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  const i = segment(x, xs);
  const t = x - xs[i];
  const b = (3 * s[i] - d[i + 1] - 2 * d[i]) / h[i];
  const c = (d[i + 1] + d[i] - 2 * s[i]) / (h[i] * h[i]);
  return ys[i] + t * (d[i] + t * (b + t * c));
}


/** A cubic Hermite method's derivatives at the nodes, with the X steps and secant slopes. */
type Derivatives = (xs: number[], ys: number[]) => { d: number[]; h: number[]; s: number[] };

/** The derivatives, then Hyman's filter on them. */
const filtered =
  (derivatives: Derivatives): Derivatives =>
  (xs, ys) => {
    const { d, h, s } = derivatives(xs, ys);
    return { d: hymanFilter(d, h, s), h, s };
  };

/**
 * The cubic Hermite methods (NATURALCUBICSPLINE, MONOTONICCUBICNATURALSPLINE,
 * HERMITE, MONOTONICHERMITE, AKIMA) need strictly increasing X. With a
 * duplicate X (golden d9, X 1,2,4,4,7,10) an X of the list still gives its Y
 * (the first of duplicates), anything else is ERROR 8795.
 */
// LSEG: Adfin (22038) AdInterp (golden d9, for each of these five methods: 18 of 23 points 8795, the 5 nodes numeric)
function strictHermite(derivatives: Derivatives): Interpolator {
  return (x, xs, ys) => {
    const node = xs.indexOf(x);
    if (node >= 0) return ys[node];
    for (let i = 1; i < xs.length; i++) if (xs[i] === xs[i - 1]) return { error: NOT_SORTED };
    const { d, h, s } = derivatives(xs, ys);
    return hermiteAt(x, xs, ys, d, h, s);
  };
}

/**
 * NATURALCUBICSPLINE: the natural cubic spline (second derivative 0 at both
 * ends), its end cubics continued outside the range. The spline is solved in
 * its first derivatives and evaluated piece by piece as y_i + t (a + t (b + t c)):
 * the form that gives LSEG's last digits (the second-derivative form misses 11
 * of 52 goldens).
 * MONOTONICCUBICNATURALSPLINE: the same with Hyman's monotonicity filter on its
 * derivatives: where the data is monotone the curve is too (flat on a plateau),
 * elsewhere it is the natural spline.
 */
// LSEG: Adfin (22038) AdInterp IM:NATURALCUBICSPLINE / IM:MONOTONICCUBICNATURALSPLINE (behaviour and arithmetic from the dense goldens d1, d2b, d4-d9)
const naturalCubicSpline = strictHermite(naturalDerivatives);
const monotonicCubicNaturalSpline = strictHermite(filtered(naturalDerivatives));

/**
 * The natural cubic spline's second derivatives, as Numerical Recipes'
 * `spline` solves them (the tridiagonal system in the second derivatives,
 * decomposed forward, substituted back).
 */
function naturalSecondDerivatives(xs: number[], ys: number[]): number[] {
  const n = xs.length;
  const m = new Array<number>(n).fill(0);
  const u = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const sig = (xs[i] - xs[i - 1]) / (xs[i + 1] - xs[i - 1]);
    const p = sig * m[i - 1] + 2;
    m[i] = (sig - 1) / p;
    const jump = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]) - (ys[i] - ys[i - 1]) / (xs[i] - xs[i - 1]);
    u[i] = ((6 * jump) / (xs[i + 1] - xs[i - 1]) - sig * u[i - 1]) / p;
  }
  for (let i = n - 2; i >= 0; i--) m[i] = m[i] * m[i + 1] + u[i];
  return m;
}

/**
 * The second derivatives of CUBS/CUBX at every point: the natural spline of
 * the points with the later of duplicate X values left out, and 0 at those.
 * Each piece is then evaluated between consecutive points of the full list,
 * with its own Ys (golden d9, X 1,2,4,4,7,10, Y 1..6: (2,4) runs from Y 2 to
 * 3 with the system's curvatures at 2 and 4, (4,7) from Y 4 to 5 with
 * curvature 0 at its start; golden d24, X 1,2,10,10: the spline of the first
 * three points). Without a repeated X it is naturalSecondDerivatives itself.
 */
function cubsSecondDerivatives(xs: number[], ys: number[]): number[] {
  const kept = xs.map((_, k) => k).filter((k) => k === 0 || xs[k] !== xs[k - 1]);
  const mKept = naturalSecondDerivatives(
    kept.map((k) => xs[k]),
    kept.map((k) => ys[k]),
  );
  const m = new Array<number>(xs.length).fill(0);
  kept.forEach((k, j) => (m[k] = mKept[j]));
  return m;
}

/**
 * The natural spline of CUBS/CUBX in the range, in its second-derivative form
 * (Numerical Recipes' `splint`): A y_i + B y_{i+1} + ((A^3 - A) M_i + (B^3 - B) M_{i+1}) h^2 / 6,
 * with A = (x_{i+1} - x) / h and B = (x - x_i) / h. This form gives LSEG's last
 * digits; the first-derivative form of NATURALCUBICSPLINE misses 4 of 29 d1 points.
 */
function cubsInside(x: number, xs: number[], ys: number[], m: number[]): number {
  const i = segment(x, xs);
  const h = xs[i + 1] - xs[i];
  const a = (xs[i + 1] - x) / h;
  const b = (x - xs[i]) / h;
  return a * ys[i] + b * ys[i + 1] + ((a * a * a - a) * m[i] + (b * b * b - b) * m[i + 1]) * (h * h) / 6;
}

/**
 * The natural cubic spline, flat outside the range. A node gives its own Y,
 * the first of duplicates (d24: 3 at x = 10); above the range, the last Y
 * (d24: 4). Only for curves cubsMatched accepts.
 */
// LSEG: Adfin (22038) AdInterp IM:CUBS (behaviour and arithmetic from the dense goldens d1, d4, dates, d9 and d24)
function cubs(x: number, xs: number[], ys: number[]): number {
  const last = xs.length - 1;
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  if (x < xs[0]) return ys[0];
  if (x > xs[last]) return ys[last];
  return cubsInside(x, xs, ys, cubsSecondDerivatives(xs, ys));
}

/**
 * CUBS in the range; outside it, the line of the spline's slope at the end
 * point. Past a repeated last X that slope is 1/0, and the answer not a finite
 * number: Excel's #N/A in LSEG (golden d24 at 10.5 and 12; see adInterp).
 */
// LSEG: Adfin (22038) AdInterp IM:CUBX (behaviour and arithmetic from the dense goldens d1, d4, dates, d9 and d24)
function cubx(x: number, xs: number[], ys: number[]): number {
  const last = xs.length - 1;
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  const m = cubsSecondDerivatives(xs, ys);
  if (x < xs[0]) {
    const h = xs[1] - xs[0];
    const slope = (ys[1] - ys[0]) / h - (h * (2 * m[0] + m[1])) / 6;
    return ys[0] + slope * (x - xs[0]);
  }
  if (x > xs[last]) {
    const h = xs[last] - xs[last - 1];
    const slope = (ys[last] - ys[last - 1]) / h + (h * (m[last - 1] + 2 * m[last])) / 6;
    return ys[last] + slope * (x - xs[last]);
  }
  return cubsInside(x, xs, ys, m);
}

/**
 * The curves on which CUBS/CUBX are proven exact: no Y of 0, and the first X
 * not repeated. A repeated X inside (d9) or at the end (d24) is exact
 * (cubsSecondDerivatives); a repeated first X has no golden. On a Y of 0 LSEG
 * does something EAV cannot reproduce yet (captures interp_dense_cubs_out.json
 * and interp_dense_edge_out.json): a Y of 0 is left out of the spline (d2b,
 * d5, d6), but two leading zeros give 0 between them and the first non-zero Y
 * (d7), and a trailing zero gives ERROR 9346 past the last non-zero Y (d8).
 */
// LSEG: Adfin (22038) AdInterp IM:CUBS / IM:CUBX (the inputs the goldens prove; see docs/lseg-golden/HANDOFF.md)
function cubsMatched(xs: number[], ys: number[]): boolean {
  return !ys.includes(0) && xs[0] !== xs[1];
}

/** The X steps h_i and the secant slopes S_i of the points. */
function steps(xs: number[], ys: number[]): { h: number[]; s: number[] } {
  const h: number[] = [];
  const s: number[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    h.push(xs[i + 1] - xs[i]);
    s.push((ys[i + 1] - ys[i]) / h[i]);
  }
  return { h, s };
}

/**
 * The Bessel derivatives (the slopes of the three-point parabolas). At an inner
 * node, the slope there of the parabola through the node and its two
 * neighbours: d_i = (h_{i-1} S_i + h_i S_{i-1}) / (h_{i-1} + h_i). At an end,
 * the slope there of the parabola through the three end points:
 * d_0 = ((2 h_0 + h_1) S_0 - h_0 S_1) / (h_0 + h_1), and the mirror image at the
 * last point. With 2 points, both derivatives are the secant slope (the line,
 * golden p2).
 */
function besselDerivatives(xs: number[], ys: number[]): { d: number[]; h: number[]; s: number[] } {
  const n = xs.length;
  const { h, s } = steps(xs, ys);
  if (n === 2) return { d: [s[0], s[0]], h, s };
  const d = new Array<number>(n);
  for (let i = 1; i < n - 1; i++) d[i] = (h[i - 1] * s[i] + h[i] * s[i - 1]) / (h[i - 1] + h[i]);
  d[0] = ((2 * h[0] + h[1]) * s[0] - h[0] * s[1]) / (h[0] + h[1]);
  d[n - 1] = ((2 * h[n - 2] + h[n - 3]) * s[n - 2] - h[n - 2] * s[n - 3]) / (h[n - 2] + h[n - 3]);
  return { d, h, s };
}

/**
 * HERMITE: the cubic Hermite pieces on the Bessel derivatives, the end pieces
 * continued outside the range (on d1 they are the parabolas through the three
 * end points). A node gives its own Y. Evaluated by hermiteAt, piece by piece
 * as y_i + t (a + t (b + t c)) from the left end of the piece, the form shared
 * with NATURALCUBICSPLINE: the Hermite-basis and power forms miss 8 to 10 of
 * the 58 goldens of d1 and d2b.
 * MONOTONICHERMITE: the same with Hyman's monotonicity filter (hymanFilter, as
 * MONOTONICCUBICNATURALSPLINE) on the derivatives. The two coincide on d1,
 * where the filter changes nothing, and differ on d2b (flat on its plateau).
 * A Y of 0 is an ordinary value (d2b, d6, d7, d8); a repeated X is 8795 off
 * the nodes (d9).
 */
// LSEG: Adfin (22038) AdInterp IM:HERMITE / IM:MONOTONICHERMITE (behaviour and arithmetic from the goldens d1, d2b, d6-d9, p2, p3)
const hermite = strictHermite(besselDerivatives);
const monotonicHermite = strictHermite(filtered(besselDerivatives));

/**
 * Akima's weighted mean, given its numerator w1 p + w2 q (w1, w2 >= 0), or
 * `tie` when both weights are 0.
 */
function akimaMean(numerator: number, w1: number, w2: number, tie: number): number {
  return w1 + w2 === 0 ? tie : numerator / (w1 + w2);
}

/**
 * Akima's derivatives. At an inner node i (2 <= i <= n-3), from the slopes
 * around it: d_i = (|S_{i+1} - S_i| S_{i-1} + |S_{i-1} - S_{i-2}| S_i) / (|S_{i+1} - S_i| + |S_{i-1} - S_{i-2}|).
 * Beyond the ends the slopes are LSEG's: products, not Akima's 2 S_0 - S_1
 * (the goldens force -3.75 = 2 * 1.5 * -1.25 on d1):
 *   left:  S_{-1} = 2 S_0 S_1,           S_{-2} = 4 S_0 S_0 S_1;
 *   right: S_{n-1} = 2 S_{n-2} S_{n-3},  S_n = 4 S_{n-2} S_{n-2} S_{n-3}.
 * The four end nodes (0, 1, n-2, n-1) use the same mean with these products
 * written into it and computed where they stand, left to right
 * (`|w| * 2 * a * b`, not `|w| * (2 * a * b)`): the form that gives LSEG's last
 * digits (with the slopes computed first, 5 points above the range on d1
 * miss; expanding the last piece around the last node mends d1 but misses 3
 * on d6 and d8). With 3 points, node 1 is computed last, as node
 * n-2, and the slope before S_0 that its formula reads counts as 0 (golden p3:
 * d_1 = 6/11, where Akima's mean with S_{-1} gives -18/23). With 2 points,
 * the line (golden p2).
 * When both weights of a node are 0 (goldens d10-d14, X 1..6): node 1 takes
 * S_1 and node n-2 takes S_{n-3}, the slope on the inner side (d12, Y
 * 0,2,2.5,3,4,6: d_1 = 0.5 = S_1, not the mean 1.25 nor the sum 2.5; d13, Y
 * 0,1,3,3.5,4,6: d_4 = 0.5 = S_3); an inner node takes the mean of its two
 * slopes (d14, Y 1,2,3,5,7,9: d_2 = 1.5). At nodes 0 and n-1 both weights are
 * 0 only where the two slopes are equal.
 */
function akimaDerivatives(xs: number[], ys: number[]): { d: number[]; h: number[]; s: number[] } {
  const n = xs.length;
  const { h, s: S } = steps(xs, ys);
  if (n === 2) return { d: [S[0], S[0]], h, s: S };
  const d = new Array<number>(n);
  let [w1, w2] = [Math.abs(S[1] - S[0]), Math.abs(2 * S[0] * S[1] - 4 * S[0] * S[0] * S[1])];
  d[0] = akimaMean(w1 * 2 * S[0] * S[1] + w2 * S[0], w1, w2, S[0]);
  [w1, w2] = [Math.abs(S[2] - S[1]), Math.abs(S[0] - 2 * S[0] * S[1])];
  d[1] = akimaMean(w1 * S[0] + w2 * S[1], w1, w2, S[1]);
  for (let i = 2; i < n - 2; i++) {
    [w1, w2] = [Math.abs(S[i + 1] - S[i]), Math.abs(S[i - 1] - S[i - 2])];
    d[i] = akimaMean(w1 * S[i - 1] + w2 * S[i], w1, w2, (S[i - 1] + S[i]) / 2);
  }
  const [a, b, c] = [S[n - 2], S[n - 3], n >= 4 ? S[n - 4] : 0];
  [w1, w2] = [Math.abs(2 * a * b - a), Math.abs(b - c)];
  d[n - 2] = akimaMean(w1 * b + w2 * a, w1, w2, b);
  [w1, w2] = [Math.abs(4 * a * a * b - 2 * a * b), Math.abs(a - b)];
  d[n - 1] = akimaMean(w1 * a + w2 * 2 * a * b, w1, w2, a);
  return { d, h, s: S };
}

/**
 * AKIMA: cubic Hermite pieces on Akima's derivatives, the end pieces continued
 * outside the range, evaluated by hermiteAt (as HERMITE). A Y of 0 is an
 * ordinary value (d2b, d6, d7, d8); a repeated X is 8795 off the nodes (d9).
 * The akima_pick captures (8 points where the nearer-node form would differ)
 * confirm the left-node form inside the range.
 */
// LSEG: Adfin (22038) AdInterp IM:AKIMA (behaviour and arithmetic from the goldens d1, d2b, d6-d14, p2, p3 and the akima_pick captures)
const akima = strictHermite(akimaDerivatives);

// LSEG: Adfin (22038) AdInterp IM:LOG (its error text, from the goldens d1 and d2b)
const NEGATIVE = "ERROR 9348 - Interpolation method not Applicable because input rate (or calculated rate) is negative.";

/**
 * LOG: linear in log Y, so geometric between two nodes (√(2 · 3.5) half way
 * from 1 to 2 on d1) and continued from the end segments outside the range
 * (not flat). A node gives its own Y, whatever its sign; elsewhere a segment
 * with a Y of 0 or below, the extrapolated end segments included, is ERROR
 * 9348 (d1: only the two segments around its -0.5; d2b, d6, d7, d8: the
 * segments that touch a 0). Of duplicate X values only the first point counts
 * (d9, X 1,2,4,4,7,10, Y 1..6: from (4,3) to (7,5), unlike LIN).
 * Written as LIN's slope and intercept on the logs, exp(a + b x): the form that
 * gives LSEG's last digits (y0 (y1/y0)^w, the weighted logs and the other
 * forms miss 15 to 26 of the 80 goldens of d1, d3, d2b), with log and exp
 * correctly rounded as LSEG's C runtime gives them on every golden (V8's
 * Math.log and Math.exp miss 19 of the 175 numeric goldens by one unit in the
 * 15th decimal).
 */
// LSEG: Adfin (22038) AdInterp IM:LOG (behaviour and arithmetic from the dense goldens d1, d3, d2b, d6-d9, p2, p3)
function logLinear(x: number, xs: number[], ys: number[]): number | { error: string } {
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  const kept = xs.map((_, k) => k).filter((k) => k === 0 || xs[k] !== xs[k - 1]);
  const [px, py] = [kept.map((k) => xs[k]), kept.map((k) => ys[k])];
  const i = segment(x, px);
  const [x0, x1, y0, y1] = [px[i], px[i + 1], py[i], py[i + 1]];
  if (y0 <= 0 || y1 <= 0) return { error: NEGATIVE };
  const [l0, l1] = [crLog(y0), crLog(y1)];
  const slope = (l1 - l0) / (x1 - x0);
  const intercept = (l0 * x1 - l1 * x0) / (x1 - x0);
  return crExp(intercept + slope * x);
}

/**
 * The points CUBD/CUBR work on around x: up to four, taken from the start,
 * with the later of duplicate X values left out, sliding on until two of them
 * lie above x (or the points run out). `below` is how many of them lie below
 * x: 2 for an inner piece (window i-1..i+2), 1 for the first piece, 3 for the
 * last, 0 and 4 outside the range. An X equal to x is a node: its own Y.
 */
// LSEG: Adfin (22038) AdInterp IM:CUBD/IM:CUBR (the window locator, read from its compiled code)
function cubicWindow(x: number, xs: number[]): { node: number } | { points: number[]; below: number } {
  const points: number[] = [];
  let below = 0;
  let above = 0;
  for (let i = 0; i < xs.length; i++) {
    if (points.length > 0 && xs[points[points.length - 1]] === xs[i]) continue;
    if (points.length === 4) {
      points.shift();
      below--;
    }
    points.push(i);
    if (x === xs[i]) return { node: i };
    if (x > xs[i]) below++;
    else above++;
    if (points.length === 4 && above >= 2) break;
  }
  return { points, below };
}

/**
 * The end piece of CUBD: the parabola on three points (a, b, c) whose slope at
 * b is the mean of the two secants, so 0.5 / (x_b - x_a) times their difference
 * as the x² coefficient. It runs from a, which is the last point at the right
 * end (c, b, a taken backwards).
 */
// LSEG: Adfin (22038) AdInterp IM:CUBD (its three-point end piece, read from its compiled code)
function cubicEnd(x: number, xs: number[], ys: number[], [a, b, c]: number[]): number {
  const [xa, xb, xc] = [xs[a], xs[b], xs[c]];
  const s0 = (ys[b] - ys[a]) / (xb - xa);
  const c2 = (0.5 / (xb - xa)) * ((ys[c] - ys[b]) / (xc - xb) - s0);
  const c1 = s0 - c2 * (xa + xb);
  return c2 * x * x + c1 * x + (ys[a] - c1 * xa - xa * c2 * xa);
}

/** Half the secant slope from a to b, as Adfin divides it: by (h + h). */
const halfSecant = (xs: number[], ys: number[], a: number, b: number) => {
  const h = xs[b] - xs[a];
  return (ys[b] - ys[a]) / (h + h);
};

/**
 * The cubic from (x0, y0) to (x1, y1) with slopes d0 and d1 there, solved in
 * powers of x itself (not of x - x0) and evaluated as c0 + (c1 x + (c3 x³ +
 * c2 x²)). The large powers are why LSEG's values sit up to 1.4e-14 from the
 * exact curve; this order of operations gives its last digits.
 */
// LSEG: Adfin (22038) AdInterp IM:CUBD/IM:CUBR (the piece evaluator, read from its compiled code)
function cubicPiece(x: number, x0: number, x1: number, y0: number, y1: number, d0: number, d1: number): number {
  const [i0, i1] = [x0 * (3 * x0), x1 * (3 * x1)];
  const [sq0, sq1] = [x0 * x0, x1 * x1];
  const [cu0, cu1] = [x0 * sq0, x1 * sq1];
  const [tw0, tw1] = [x0 + x0, x1 + x1];
  const span2 = tw0 - tw1;
  const span = x0 - x1;
  const v = (d0 - d1) / span2;
  const k = tw1 - (sq0 - sq1) / span;
  const q = (i0 - i1) / span2;
  const c3 = (v - (d1 - (y0 - y1) / span) / k) / (q - (i1 - (cu0 - cu1) / span) / k);
  const c2 = v - q * c3;
  const c1 = d0 - i0 * c3 - tw0 * c2;
  return y0 - cu0 * c3 - sq0 * c2 - x0 * c1 + (c1 * x + (c3 * x * x * x + c2 * x * x));
}

/**
 * CUBD and CUBR: cubic pieces whose slope at a node is the sum of the two
 * neighbouring half secants. CUBD (flat = false) ends in the three-point
 * parabola of cubicEnd, continued outside the range; CUBR (flat = true) has
 * slope 0 at the first and last points and is flat outside. With three points
 * both are cubicEnd's parabola (from the left below the middle point, from the
 * right above it), with two a straight line; CUBR stays flat outside.
 */
// LSEG: Adfin (22038) AdInterp IM:CUBD/IM:CUBR (arithmetic read from its compiled code; exact on the dense goldens d1 and d2b)
function cubicFamily(flat: boolean): Interpolator {
  return (x, xs, ys) => {
    const found = cubicWindow(x, xs);
    if ("node" in found) return ys[found.node];
    const { points, below } = found;
    const first = points[0];
    const last = points[points.length - 1];
    if (points.length === 1) return ys[first];
    if (flat && below === 0) return ys[first];
    if (flat && below === points.length) return ys[last];
    if (points.length === 2) {
      const [x0, x1, y0, y1] = [xs[first], xs[last], ys[first], ys[last]];
      return ((y1 - y0) / (x1 - x0)) * x + (x1 * y0 - y1 * x0) / (x1 - x0);
    }
    if (points.length === 3) return cubicEnd(x, xs, ys, below <= 1 ? points : [...points].reverse());
    const [w0, w1, w2, w3] = points;
    if (!flat && below <= 1) return cubicEnd(x, xs, ys, [w0, w1, w2]);
    if (!flat && below >= 3) return cubicEnd(x, xs, ys, [w3, w2, w1]);
    if (below === 1) {
      const d1 = halfSecant(xs, ys, w1, w2) + halfSecant(xs, ys, w0, w1);
      return cubicPiece(x, xs[w0], xs[w1], ys[w0], ys[w1], 0, d1);
    }
    if (below === 3) {
      const d0 = halfSecant(xs, ys, w2, w3) + halfSecant(xs, ys, w1, w2);
      return cubicPiece(x, xs[w2], xs[w3], ys[w2], ys[w3], d0, 0);
    }
    const middle = halfSecant(xs, ys, w1, w2);
    const d0 = middle + halfSecant(xs, ys, w0, w1);
    const d1 = halfSecant(xs, ys, w2, w3) + middle;
    return cubicPiece(x, xs[w1], xs[w2], ys[w1], ys[w2], d0, d1);
  };
}

/**
 * The ported methods, by their full code. Each is added with its goldens.
 * CUBD/CUBR follow Adfin's arithmetic as read from its compiled code (the
 * user's exception, 2026-10-30, to the design's rule of writing each method
 * from its mathematical definition): no other order of operations tried gave
 * their last digits.
 * TSPLINE/TSPLINEX (interpTension.ts) and SPLREP (interpSmoothing.ts) live in
 * their own files. The full list of what is not matched, by code and input
 * class, is in docs/lseg-golden/HANDOFF.md ("EAV.RDP.INTERP: methods not matched").
 */
export const METHODS: Record<string, Interpolator> = {
  "IM:LIN": lin,
  "IM:STEP": step,
  "IM:STEPX": stepx,
  "IM:STEPRCYES": steprcyes,
  "IM:CUBD": cubicFamily(false),
  "IM:CUBR": cubicFamily(true),
  "IM:NATURALCUBICSPLINE": naturalCubicSpline,
  "IM:MONOTONICCUBICNATURALSPLINE": monotonicCubicNaturalSpline,
  "IM:CUBS": cubs,
  "IM:CUBX": cubx,
  "IM:HERMITE": hermite,
  "IM:MONOTONICHERMITE": monotonicHermite,
  "IM:AKIMA": akima,
  "IM:LOG": logLinear,
  "IM:TSPLINE": tspline,
  "IM:TSPLINEX": tsplinex,
  "IM:SPLREP": splrep,
};

/**
 * Ported methods that match LSEG only on some curves: for the others they are
 * answered as not ported ("not available yet"). CUBS/CUBX: no Y of 0, no
 * repeated first X; TSPLINE/TSPLINEX: no Y of 0 between non-zero Y on
 * distinct X, not every Y 0, no repeated first X; SPLREP: X spanning at most
 * 15, no two knots placed by fpknot, a search for p that converges.
 */
const MATCHED_ON: Record<string, (xs: number[], ys: number[]) => boolean> = {
  "IM:CUBS": cubsMatched,
  "IM:CUBX": cubsMatched,
  // LOG keeps the first of duplicate X values: a list of one X repeated leaves no segment (no golden).
  "IM:LOG": (xs) => xs.some((x) => x !== xs[0]),
  "IM:TSPLINE": tsplineMatched,
  "IM:TSPLINEX": tsplineMatched,
  "IM:SPLREP": splrepMatched,
};

const hasOwn = (table: object, key: string) => Object.prototype.hasOwnProperty.call(table, key);

/**
 * Whether `method` is ported: an own entry of METHODS (not an inherited name
 * such as "toString"). Given the points, also whether it is ported for them
 * (MATCHED_ON: CUBS/CUBX are not for a curve with a Y of 0 or a repeated first X, ...).
 */
export function isPorted(method: string, xs?: number[], ys?: number[]): boolean {
  if (!hasOwn(METHODS, method)) return false;
  if (xs === undefined || ys === undefined || !hasOwn(MATCHED_ON, method)) return true;
  return MATCHED_ON[method](xs, ys);
}

/**
 * Adfin's check of the X list, made before the method is looked at: a 0 is a
 * null date (8788), and an X smaller than the one before it is unsorted
 * (8795). Equal X values are accepted.
 */
// LSEG: Adfin (22038) AdInterp (the DateArray checks; their order is not captured, 8788 first as the goldens allow)
export function inputError(xs: number[]): string | undefined {
  if (xs.includes(0)) return NULL_DATE;
  for (let i = 1; i < xs.length; i++) if (xs[i] < xs[i - 1]) return NOT_SORTED;
  return undefined;
}

/**
 * LSEG's `AdInterp`: the Y value at `x` on the points (xs, ys) by `method`
 * (a full code, "IM:LIN"), rounded as LSEG's executor rounds, or Adfin's
 * error text (what follows "AdInterp evaluation error: "). It assumes the
 * processor's checks have passed. A code LSEG accepts that is not ported (in
 * METHODS, and for these points) gets ERROR 9042 here, which is wrong for
 * LSEG: interpLocal (interp.ts) answers such a code itself and never calls this.
 * A value that is not a finite double is NaN, which the layout shows as
 * Excel's #N/A: LSEG shows #N/A where CUBX's end slope is 1/0 (golden d24,
 * above a repeated last X), and an infinity would reach Excel as #NUM!.
 */
// LSEG: InterpExecutor (54902).execute (the rounding), Adfin (22038) AdInterp
export function adInterp(x: number, xs: number[], ys: number[], method: string): { value: number } | { error: string } {
  const refused = inputError(xs);
  if (refused) return { error: refused };
  if (!isPorted(method, xs, ys)) return { error: NOT_SUPPORTED };
  const y = METHODS[method](x, xs, ys);
  if (typeof y !== "number") return y;
  return { value: Number.isFinite(y) ? parseFloat(y.toFixed(15)) : NaN };
}
